/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { apiClient } from './client.js';

export type HomeTaskCategory =
  | 'social'
  | 'sensory'
  | 'cognitive'
  | 'communication'
  | 'motor'
  | 'other';
export type HomeTaskFrequency = 'daily' | 'weekly';
export type CompletionStatus = 'done' | 'partial' | 'skipped';

export interface HomeTask {
  readonly id: string;
  readonly clientId: string;
  readonly goalId: string | null;
  readonly title: string;
  readonly category: HomeTaskCategory;
  readonly frequency: HomeTaskFrequency;
  readonly instructions: string | null;
  readonly active: boolean;
  readonly todayStatus: CompletionStatus | null;
}

export interface CreateHomeTaskPayload {
  readonly clientId: string;
  readonly title: string;
  readonly category: HomeTaskCategory;
  readonly frequency?: HomeTaskFrequency;
  readonly instructions?: string;
  readonly goalId?: string;
}

export interface CompletePayload {
  readonly status: CompletionStatus;
  readonly note?: string;
}

export interface AchievementBadge {
  readonly key: string;
  readonly earned: boolean;
}

export interface Achievements {
  readonly totalCompleted: number;
  readonly currentStreak: number;
  readonly longestStreak: number;
  readonly badges: readonly AchievementBadge[];
}

const BASE = '/home-tasks';

export const homeApi = {
  async list(clientId: string): Promise<readonly HomeTask[]> {
    const { data } = await apiClient.get<readonly HomeTask[]>(BASE, { params: { clientId } });
    return data;
  },
  async achievements(clientId: string): Promise<Achievements> {
    const { data } = await apiClient.get<Achievements>(`${BASE}/achievements`, {
      params: { clientId },
    });
    return data;
  },
  async create(payload: CreateHomeTaskPayload): Promise<HomeTask> {
    const { data } = await apiClient.post<HomeTask>(BASE, payload);
    return data;
  },
  async complete(id: string, payload: CompletePayload): Promise<void> {
    await apiClient.post(`${BASE}/${id}/complete`, payload);
  },
};
